/**
 * Reconciliation API client
 *
 * Phase 4-5 / Stream 4 / Plan §12 / Design §5.9 C7 财务对账（占位）.
 *
 * ⚠️ V1 占位实现：CSV 解析 + diff 算法详细 spec 另立
 * (project-docs/specs/2026-05-23-finance-reconciliation-design.md，待补)
 */
import { apiClient } from './client';

export type ReconciliationStatus = 'uploaded' | 'diff_done' | 'diff_failed';
export type MarkRecordType = 'payment' | 'refund' | 'voucher' | 'wx_bill';

export interface DiffSummary {
  matched: number;
  mismatched: number;
  total: number;
}

export interface ReconciliationBatchDto {
  batchId: string;
  billMonth: string;
  fileName: string;
  fileSize: number;
  fileHash: string | null;
  status: ReconciliationStatus;
  diffSummary: DiffSummary | null;
  uploadedAt: string;
  uploadedBy: string;
  diffTriggeredAt: string | null;
  diffTriggeredBy: string | null;
  diffCompletedAt: string | null;
  notes: string | null;
}

export interface ReconciliationMarkDto {
  markId: string;
  batchId: string;
  recordId: string;
  recordType: MarkRecordType;
  markReason: string;
  markedBy: string;
  markedAt: string;
}

export interface ListBatchesFilter {
  billMonth?: string;
  status?: ReconciliationStatus;
}

export interface ListBatchesResponse {
  items: ReconciliationBatchDto[];
  total: number;
}

export interface BatchDetailResponse {
  batch: ReconciliationBatchDto;
  marks: ReconciliationMarkDto[];
}

export interface UploadBatchInput {
  billMonth: string;
  fileName: string;
  fileSize: number;
  fileHash?: string | null;
  notes?: string | null;
}

export interface AddMarkInput {
  recordId: string;
  recordType: MarkRecordType;
  markReason: string;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

export async function listBatches(
  filter: ListBatchesFilter = {},
): Promise<ListBatchesResponse> {
  const params: Record<string, string> = {};
  if (filter.billMonth) params.billMonth = filter.billMonth;
  if (filter.status) params.status = filter.status;
  const resp = await apiClient.get<ServiceResponse<ListBatchesResponse>>(
    '/reconciliation/batches', { params },
  );
  return resp.data.data;
}

export async function getBatchDetail(batchId: string): Promise<BatchDetailResponse> {
  const resp = await apiClient.get<ServiceResponse<BatchDetailResponse>>(
    `/reconciliation/batches/${encodeURIComponent(batchId)}`,
  );
  return resp.data.data;
}

export async function uploadBatch(input: UploadBatchInput): Promise<ReconciliationBatchDto> {
  const resp = await apiClient.post<ServiceResponse<ReconciliationBatchDto>>(
    '/reconciliation/batches', input,
  );
  return resp.data.data;
}

/**
 * 触发 diff（占位）。后端立即 flip uploaded→diff_done + 假 summary。
 */
export async function triggerDiff(batchId: string): Promise<ReconciliationBatchDto> {
  const resp = await apiClient.post<ServiceResponse<ReconciliationBatchDto>>(
    `/reconciliation/batches/${encodeURIComponent(batchId)}/trigger-diff`,
    {},
  );
  return resp.data.data;
}

export async function addMark(
  batchId: string, input: AddMarkInput,
): Promise<ReconciliationMarkDto> {
  const resp = await apiClient.post<ServiceResponse<ReconciliationMarkDto>>(
    `/reconciliation/batches/${encodeURIComponent(batchId)}/marks`,
    input,
  );
  return resp.data.data;
}

/** 导出占位 CSV — 返回 URL，前端 anchor 直接 download. */
export function batchExportUrl(batchId: string): string {
  return `/admin/api/v1/reconciliation/batches/${encodeURIComponent(batchId)}/export`;
}
